import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { formatAmount } from '@/lib/utils';

import type { GapBucket } from './types';

/**
 * Composed gap chart:
 *   - Vertical bars: net gap per bucket (green when ≥0, red when negative)
 *   - Line overlay: cumulative gap across buckets
 *   - Reference line at 0 highlights shortfalls
 *
 * BigDecimal-from-server values are converted to JS Number for plotting only.
 * That coercion is safe up to ~15 significant digits — fine for ALM amounts in
 * MAD/EUR/USD. Tooltips render the original server string via formatAmount()
 * so users always see the precise value.
 */
interface ChartDatum {
  bucketIndex: number;
  label: string;
  netGap: number;
  cumulativeGap: number;
  /** Originals for tooltip display (preserves server precision). */
  netGapStr: string;
  cumulativeGapStr: string;
  assetsInflowStr: string;
  liabilitiesOutflowStr: string;
}

const COLOR_POSITIVE = 'hsl(142 71% 45%)'; // emerald-600 ish
const COLOR_NEGATIVE = 'hsl(0 72% 51%)';   // matches --destructive
const COLOR_LINE     = 'hsl(221 83% 53%)'; // matches --primary

export function GapBucketsChart({ buckets }: { buckets: GapBucket[] }) {
  const data = useMemo<ChartDatum[]>(
    () =>
      buckets.map((b) => ({
        bucketIndex: b.bucketIndex,
        label: b.label,
        netGap: Number(b.netGap),
        cumulativeGap: Number(b.cumulativeGap),
        netGapStr: b.netGap,
        cumulativeGapStr: b.cumulativeGap,
        assetsInflowStr: b.assetsInflow,
        liabilitiesOutflowStr: b.liabilitiesOutflow,
      })),
    [buckets],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Gap by maturity bucket</CardTitle>
        <CardDescription>
          Bars: net gap per bucket (red below zero). Line: cumulative gap across maturities.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No buckets to plot — run a calculation to populate the chart.
          </p>
        ) : (
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => compactAmount(v as number)}
                  width={70}
                />
                <ReferenceLine y={0} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--muted) / 0.5)' }}
                  content={<GapTooltip />}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  iconType="circle"
                />
                <Bar dataKey="netGap" name="Net gap" radius={[4, 4, 0, 0]}>
                  {data.map((d) => (
                    <Cell
                      key={d.bucketIndex}
                      fill={d.netGap >= 0 ? COLOR_POSITIVE : COLOR_NEGATIVE}
                    />
                  ))}
                </Bar>
                <Line
                  type="monotone"
                  dataKey="cumulativeGap"
                  name="Cumulative gap"
                  stroke={COLOR_LINE}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Compact form for axis ticks: 1.2M, 350K, etc. */
function compactAmount(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${sign(v)}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign(v)}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign(v)}${(abs / 1_000).toFixed(1)}K`;
  return `${sign(v)}${abs.toFixed(0)}`;
}

function sign(v: number): string {
  return v < 0 ? '-' : '';
}

function GapTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload as ChartDatum;
  return (
    <div className="rounded-md border bg-popover p-3 text-xs shadow-md">
      <p className="mb-1.5 text-sm font-medium text-popover-foreground">{d.label}</p>
      <Row label="Assets inflow" value={formatAmount(d.assetsInflowStr)} />
      <Row label="Liabilities outflow" value={formatAmount(d.liabilitiesOutflowStr)} />
      <Row
        label="Net gap"
        value={formatAmount(d.netGapStr)}
        emphasis={d.netGap < 0 ? 'negative' : 'positive'}
      />
      <Row label="Cumulative" value={formatAmount(d.cumulativeGapStr)} />
    </div>
  );
}

function Row({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: 'positive' | 'negative';
}) {
  const valueClass =
    emphasis === 'negative'
      ? 'text-destructive font-medium'
      : emphasis === 'positive'
        ? 'text-foreground font-medium'
        : 'text-muted-foreground';
  return (
    <div className="flex items-baseline justify-between gap-6">
      <span className="text-muted-foreground">{label}</span>
      <span className={'tabular-nums ' + valueClass}>{value}</span>
    </div>
  );
}
